/**
 * Money rendering for the admin dataset grids (Sherex Options, Autoriv
 * Configurator Items, PEMSERTER Price List EUR/USD, Profil InDie Options —
 * every grid mounted once per currency/region).
 *
 * This lives in ONE place on purpose. Each page previously carried its own
 * `groupSep`/`decimalSep` table and they drifted: Autoriv rendered EUR de-DE
 * style ("€54.726,19") while Sherex rendered it en-US ("€54,726.19"), so the
 * same number read two different ways in the same console. The separators are
 * fixed here rather than exposed as per-page config so that cannot recur.
 *
 * NOT the same as `customer/utils/cost-display.ts#formatMoney` — that one is
 * deliberate POC parity for the customer app and goes through `Number()` +
 * `toLocaleString`, which is both float-based and browser-locale dependent
 * (the very thing that produced the de-DE output above). Admin dataset columns
 * read raw `numeric(12,2)` / `numeric(12,4)` strings off the wire, so they
 * format with string arithmetic only — money never round-trips through a
 * float here (§L). Do not merge the two.
 */

export type Currency = "EUR" | "USD";

/** ISO-4217 -> display glyph. Currency travels via the row's region (§D.14). */
export const CURRENCY_SYMBOL: Record<Currency, string> = {
  EUR: "€",
  USD: "$",
};

/** Both currencies render en-US style: "," groups, "." decimal. */
const GROUP_SEP = ",";
const DECIMAL_SEP = ".";

/**
 * Decimal string off the wire -> "54,726.19" / "1,835.40" / "2,750.00" —
 * `formatMoney` without the currency glyph, for 2dp columns that are NOT
 * money (PEMSERTER `discountRate`, a 0–1 ratio stored as numeric(18,10)).
 *
 * Always EXACTLY 2 decimals, so a numeric column reads consistently whether
 * the source is `numeric(12,2)`, `numeric(12,4)` or `numeric(18,10)`:
 * 1835.4000 -> 1,835.40 and 999.3998 -> 999.40. Rounding is half-up (away
 * from zero) on the digit string, with the carry propagated into the integer
 * part.
 *
 * Because a 2dp cell can differ from what is stored, callers rendering a
 * wider column should also surface the untouched string (a `title` tooltip
 * on the cell) so the rounding never hides the real value.
 *
 * `null`, empty, and blank render as an em dash — never as a real value (§D.4).
 */
export const formatDecimal2dp = (
  value: string | null | undefined,
): string => {
  if (value == null || value.trim() === "") return "—";

  const [rawInt = "0", rawFrac = ""] = value.trim().split(".");
  const negative = rawInt.startsWith("-");
  let digits = (negative ? rawInt.slice(1) : rawInt).replace(/^0+(?=\d)/, "");
  let frac = rawFrac.padEnd(2, "0").slice(0, 2);

  // Half-up on the first dropped digit. BigInt keeps the carry exact when it
  // ripples past the decimal point, e.g. 0.999 -> 1.00 and 999.995 -> 1000.00.
  if ((rawFrac[2] ?? "0") >= "5") {
    const carried = (BigInt(digits + frac) + 1n).toString().padStart(3, "0");
    digits = carried.slice(0, -2);
    frac = carried.slice(-2);
  }

  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEP);
  return `${negative ? "-" : ""}${grouped}${DECIMAL_SEP}${frac}`;
};

/**
 * Decimal string off the wire -> "€54,726.19" / "$1,835.40" / "€2,750.00".
 * `formatDecimal2dp` (see its contract above) with the currency glyph
 * prefixed. Blank stays a bare em dash — never "€—".
 */
export const formatMoney = (
  value: string | null | undefined,
  currency: Currency,
): string => {
  const amount = formatDecimal2dp(value);
  return amount === "—" ? amount : `${CURRENCY_SYMBOL[currency]}${amount}`;
};
